import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import {
  BookOpen, Search, FileText, Folder, Globe, HardDrive,
  CheckCircle2, Clock, Scale, Landmark, Sparkles, Hash, ArrowRight,
} from "lucide-react";

const BYTES_UNIT = 1024 * 1024;

const CATALOG_META = {
  convention_models: { label: "Conventions modèles", icon: Sparkles, color: "#D4A017" },
  convention_demos: { label: "Conventions de démo", icon: Folder, color: "#1A3C5E" },
  normative: { label: "Référentiel normatif", icon: Landmark, color: "#1B4332" },
  jurisprudence: { label: "Jurisprudence internationale", icon: Scale, color: "#8B4513" },
  glossary: { label: "Glossaire", icon: Hash, color: "#6B7280" },
};

const CATALOG_ORDER = ["convention_models", "convention_demos", "normative", "jurisprudence", "glossary"];

export default function Library() {
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mineFilters, setMineFilters] = useState({ q: "", ext: "all", project: "all" });
  const [catalogQ, setCatalogQ] = useState("");
  const [catalogKind, setCatalogKind] = useState("all");

  const debouncedMineQ = useDebounce(mineFilters.q, 250);
  const debouncedCatalogQ = useDebounce(catalogQ, 250);

  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedMineQ) params.set("q", debouncedMineQ);
    if (mineFilters.project && mineFilters.project !== "all")
      params.set("project_id", mineFilters.project);
    if (mineFilters.ext && mineFilters.ext !== "all")
      params.set("ext", mineFilters.ext);
    api
      .get(`/library/mine${params.toString() ? `?${params.toString()}` : ""}`)
      .then((r) => setMine(r.data))
      .finally(() => setLoading(false));
  }, [debouncedMineQ, mineFilters.project, mineFilters.ext]);

  useEffect(() => {
    const url = debouncedCatalogQ
      ? `/library/catalog?q=${encodeURIComponent(debouncedCatalogQ)}`
      : "/library/catalog";
    api.get(url).then((r) => setCatalog(r.data));
  }, [debouncedCatalogQ]);

  const projectsForFilter = useMemo(() => {
    if (!mine) return [];
    const s = new Map();
    (mine.items || []).forEach((it) => {
      if (it.project?.id) s.set(it.project.id, it.project);
    });
    return [...s.values()];
  }, [mine]);

  if (loading || !mine) {
    return (
      <div className="p-6 text-sm opacity-60" data-testid="library-page-loading">
        Chargement de la bibliothèque…
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="library-page">
      <div>
        <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-2">
          <BookOpen size={28} style={{ color: "#D4A017" }} />
          Bibliothèque documentaire
        </h1>
        <p className="text-sm opacity-70 mt-1">
          Tous vos documents uploadés d'un côté, le catalogue public de référence de l'autre.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b" style={{ borderColor: "hsl(var(--border))" }}>
        <TabBtn active={tab === "mine"} onClick={() => setTab("mine")} testid="lib-tab-mine"
          icon={FileText}>
          Mes documents
        </TabBtn>
        <TabBtn active={tab === "catalog"} onClick={() => setTab("catalog")} testid="lib-tab-catalog"
          icon={Globe}>
          Catalogue public
        </TabBtn>
      </div>

      {tab === "mine" && (
        <MineTab
          data={mine}
          filters={mineFilters}
          onFilters={setMineFilters}
          projects={projectsForFilter}
        />
      )}

      {tab === "catalog" && (
        <CatalogTab
          data={catalog}
          query={catalogQ}
          onQuery={setCatalogQ}
          kind={catalogKind}
          onKind={setCatalogKind}
        />
      )}
    </div>
  );
}

/* ----------------------------- MINE ----------------------------- */

function MineTab({ data, filters, onFilters, projects }) {
  const t = data.totals || {};
  const dist = data.distribution || {};
  const items = data.items || [];

  return (
    <div className="space-y-6" data-testid="lib-mine">
      {/* KPIs */}
      <div className="grid md:grid-cols-4 gap-3">
        <Stat label="Documents" value={t.documents} icon={FileText} color="#D4A017" testid="lib-kpi-docs" />
        <Stat label="Projets" value={t.projects} icon={Folder} color="#1A3C5E" testid="lib-kpi-projects" />
        <Stat
          label="Taille totale"
          value={`${t.total_size_mb || 0} Mo`}
          icon={HardDrive}
          color="#1B4332"
          testid="lib-kpi-size"
        />
        <Stat
          label="Formats"
          value={(dist.by_ext || []).map((e) => e.name.toUpperCase()).join(" · ") || "—"}
          icon={Hash}
          color="#8B4513"
          testid="lib-kpi-formats"
        />
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="lib-mine-filters">
        <div
          className="flex items-center gap-1 border rounded-sm px-2 py-1"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <Search size={12} className="opacity-60" />
          <input
            placeholder="Recherche plein texte…"
            value={filters.q}
            onChange={(e) => onFilters({ ...filters, q: e.target.value })}
            className="bg-transparent text-xs outline-none w-64"
            data-testid="lib-mine-search"
          />
        </div>
        <SelectFilter
          value={filters.ext}
          onChange={(v) => onFilters({ ...filters, ext: v })}
          testid="lib-mine-f-ext"
          options={[
            ["all", "Tous formats"],
            ["pdf", "PDF"],
            ["docx", "DOCX"],
            ["xlsx", "XLSX"],
            ["txt", "TXT"],
          ]}
        />
        <SelectFilter
          value={filters.project}
          onChange={(v) => onFilters({ ...filters, project: v })}
          testid="lib-mine-f-project"
          options={[
            ["all", "Tous projets"],
            ...projects.map((p) => [p.id, p.name]),
          ]}
        />
      </div>

      {/* Documents list */}
      {items.length === 0 ? (
        <div className="rap-card p-10 text-center" data-testid="lib-mine-empty">
          <FileText size={32} style={{ color: "#D4A017" }} className="mx-auto mb-3" />
          <h3 className="font-display font-bold text-base mb-2">Aucun document</h3>
          <p className="text-xs opacity-70 max-w-md mx-auto mb-3">
            {t.all_documents === 0
              ? "Uploadez un document dans un projet — il apparaîtra ici automatiquement."
              : "Aucun document ne correspond aux filtres actifs."}
          </p>
          <Link to="/projects" className="inline-flex items-center gap-2 text-sm font-semibold"
            style={{ color: "#D4A017" }}>
            Mes projets <ArrowRight size={14} />
          </Link>
        </div>
      ) : (
        <div className="rap-card p-4" data-testid="lib-mine-list">
          <div className="space-y-2">
            {items.map((it) => (
              <div
                key={it.id}
                className="p-3 border rounded-sm flex items-start gap-3"
                style={{ borderColor: "hsl(var(--border))" }}
                data-testid={`lib-doc-${it.id}`}
              >
                <ExtBadge ext={it.ext} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{it.filename}</div>
                  <div className="text-[10px] opacity-60 font-mono mt-0.5 flex flex-wrap items-center gap-2">
                    <span>{(it.file_size / BYTES_UNIT).toFixed(2)} Mo</span>
                    <span>·</span>
                    <span className="flex items-center gap-1">
                      <Clock size={10} />
                      {fmtDate(it.created_at)}
                    </span>
                    {it.has_extraction && (
                      <>
                        <span>·</span>
                        <span className="flex items-center gap-1" style={{ color: "#27AE60" }}>
                          <CheckCircle2 size={10} /> extrait
                        </span>
                      </>
                    )}
                    {it.project && (
                      <>
                        <span>·</span>
                        <Link
                          to={`/projects/${it.project.id}`}
                          className="hover:underline"
                          style={{ color: "#D4A017" }}
                          data-testid={`lib-doc-project-${it.id}`}
                        >
                          {it.project.name}
                        </Link>
                        <span className="opacity-50">
                          ({it.project.country} · {it.project.sector})
                        </span>
                      </>
                    )}
                  </div>
                  {it.excerpt && (
                    <p className="text-xs opacity-70 mt-1 line-clamp-2">
                      {it.excerpt}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* --------------------------- CATALOG --------------------------- */

function CatalogTab({ data, query, onQuery, kind, onKind }) {
  if (!data) return <div className="text-xs opacity-60 py-6">Chargement…</div>;
  const t = data.totals || {};
  const items = data.items || {};

  return (
    <div className="space-y-6" data-testid="lib-catalog">
      {/* KPIs */}
      <div className="grid md:grid-cols-5 gap-3">
        {CATALOG_ORDER.map((k) => {
          const meta = CATALOG_META[k];
          return (
            <Stat
              key={k}
              label={meta.label}
              value={t[k] || 0}
              icon={meta.icon}
              color={meta.color}
              testid={`lib-cat-kpi-${k}`}
            />
          );
        })}
      </div>

      {/* Search + kind filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <div
          className="flex items-center gap-1 border rounded-sm px-2 py-1"
          style={{ borderColor: "hsl(var(--border))" }}
        >
          <Search size={12} className="opacity-60" />
          <input
            placeholder="Rechercher dans tout le catalogue…"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            className="bg-transparent text-xs outline-none w-80"
            data-testid="lib-cat-search"
          />
        </div>
        <SelectFilter
          value={kind}
          onChange={onKind}
          testid="lib-cat-kind"
          options={[
            ["all", `Toutes catégories (${t.all || 0})`],
            ...CATALOG_ORDER.map((k) => [k, `${CATALOG_META[k].label} (${t[k] || 0})`]),
          ]}
        />
      </div>

      {/* Sections */}
      {CATALOG_ORDER.filter((k) => kind === "all" || kind === k).map((k) => {
        const meta = CATALOG_META[k];
        const list = items[k] || [];
        if (list.length === 0) return null;
        const Icon = meta.icon;
        return (
          <div key={k} className="rap-card p-5" data-testid={`lib-cat-section-${k}`}>
            <h2 className="font-display font-bold text-base mb-3 flex items-center gap-2">
              <Icon size={16} style={{ color: meta.color }} />
              {meta.label}
              <span className="text-[10px] font-mono opacity-60">({list.length})</span>
            </h2>
            <div className="grid md:grid-cols-2 gap-2">
              {list.map((it) => (
                <div
                  key={it.id}
                  className="p-3 border rounded-sm"
                  style={{ borderColor: "hsl(var(--border))" }}
                  data-testid={`lib-cat-item-${it.id}`}
                >
                  <div className="font-semibold text-sm">{it.title}</div>
                  {it.subtitle && (
                    <div className="text-[10px] opacity-60 font-mono mt-0.5 truncate">
                      {it.subtitle}
                    </div>
                  )}
                  {it.snippet && (
                    <p className="text-xs opacity-75 mt-1 line-clamp-3">{it.snippet}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {Object.values(items).every((arr) => (arr || []).length === 0) && (
        <div className="rap-card p-10 text-center" data-testid="lib-cat-empty">
          <Search size={32} style={{ color: "#D4A017" }} className="mx-auto mb-3" />
          <h3 className="font-display font-bold text-base mb-1">Aucun résultat</h3>
          <p className="text-xs opacity-70">
            Essayez un autre terme ou effacez la recherche.
          </p>
        </div>
      )}
    </div>
  );
}

/* ------------------------- HELPERS ------------------------- */

function Stat({ label, value, icon: Icon, color, testid }) {
  return (
    <div className="rap-card p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-wider opacity-70">{label}</div>
        <Icon size={14} style={{ color }} />
      </div>
      <div
        className="font-mono text-xl font-bold truncate"
        style={{ color }}
        title={String(value)}
      >
        {value}
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, children, testid }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 text-sm font-semibold transition-colors"
      style={{
        borderBottom: active ? "2px solid #D4A017" : "2px solid transparent",
        color: active ? "#D4A017" : "inherit",
        opacity: active ? 1 : 0.7,
      }}
      data-testid={testid}
    >
      <Icon size={14} />
      {children}
    </button>
  );
}

function SelectFilter({ value, onChange, options, testid }) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="text-xs border rounded-sm bg-transparent px-2 py-1"
      style={{ borderColor: "hsl(var(--border))" }}
      data-testid={testid}
    >
      {options.map(([v, label]) => (
        <option key={v} value={v}>
          {label}
        </option>
      ))}
    </select>
  );
}

function ExtBadge({ ext }) {
  const colors = {
    pdf: "#C0392B",
    docx: "#1A3C5E",
    xlsx: "#1B4332",
    txt: "#6B7280",
  };
  const c = colors[ext] || "#8B4513";
  return (
    <span
      className="text-[10px] font-mono font-bold px-2 py-1 rounded-sm uppercase"
      style={{ background: `${c}20`, color: c, minWidth: 46, textAlign: "center" }}
    >
      {ext || "?"}
    </span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      day: "2-digit", month: "short", year: "numeric",
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function useDebounce(value, delay = 250) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
}
